import {
  HttpHeaders,
  HttpStatusCodes,
  MimeTypes,
  bearerAuthHeader,
} from '@climatepartner/unified-api-lib-http-utils'

import { CalculationService } from './calculation'
import { CommunicationService } from './communication'
import { Response } from './gen/function/http'
import { IError, IRequest, openApi } from './gen/function/open-api'
import { ICodeErrorCommonErrorUnauthorized } from './gen/interface/components/interface/enumeration/i-code-error-common-error-unauthorized'
import { HttpRequestFn, InterceptorContext } from './gen/interface/i-$-open-api'
import { HealthcheckService } from './healthcheck'
import { OrderService } from './order'
import { StsService } from './sts'
import {
  RawError,
  UnifiedApiBadRequestError,
  UnifiedApiConfiguration,
  UnifiedApiInternalServerError,
} from './types'

export class UnifiedApi {
  #longtermToken: string

  #authorizationStoragePath?: string

  #stsService?: StsService

  #healthCheckService?: HealthcheckService

  #orderService?: OrderService

  #calculationService?: CalculationService

  #communicationService?: CommunicationService

  constructor(options: UnifiedApiConfiguration) {
    const { longtermToken, endpointUrl, authorizationStoragePath } = options
    this.#longtermToken = longtermToken
    this.#authorizationStoragePath = authorizationStoragePath

    openApi.requestInterceptor = this.#requestInterceptor
    openApi.responseInterceptor = this.#responseInterceptor
    openApi.errorHandler = this.#errorHandler
    openApi.endpointUrl =
      endpointUrl || 'https://unified-api.climatepartner.com'
  }

  getStsService = () => {
    if (!this.#stsService) {
      this.#stsService = new StsService(
        this.#longtermToken,
        this.#authorizationStoragePath,
      )
    }

    return this.#stsService
  }

  getHealthcheckService = () => {
    if (!this.#healthCheckService) {
      this.#healthCheckService = new HealthcheckService()
    }

    return this.#healthCheckService
  }

  getOrderService = () => {
    if (!this.#orderService) {
      this.#orderService = new OrderService()
    }

    return this.#orderService
  }

  getCalculationService = () => {
    if (!this.#calculationService) {
      this.#calculationService = new CalculationService()
    }

    return this.#calculationService
  }

  getCommunicationService = () => {
    if (!this.#communicationService) {
      this.#communicationService = new CommunicationService()
    }
    return this.#communicationService
  }

  #requestInterceptor = async (request: IRequest) => {
    request.header = {
      ...request.header,
      [HttpHeaders.AUTHORIZATION]: bearerAuthHeader(
        await this.getStsService().getToken(),
      ),
    }

    return request
  }

  #responseInterceptor = async (
    request: IRequest,
    response: Response | undefined,
    retry: HttpRequestFn,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context: InterceptorContext,
    error?: IError | unknown,
  ) => {
    // the retry() function is a reference to the function that has been called
    // internally to run the request. The request object contains all parameters
    // so we can mutate them if necessary, and rety.
    if (error) {
      throw error
    }

    if (response) {
      if (response.status === HttpStatusCodes.UNAUTHORIZED) {
        if (
          response.headers[HttpHeaders.CONTENT_TYPE].startsWith(
            MimeTypes.APPLICATION_JSON,
          )
        ) {
          const body = JSON.parse(await response.text())
          if (
            body?.error?.code === ICodeErrorCommonErrorUnauthorized.JWT_EXPIRED
          ) {
            request.header = {
              ...request.header,
              [HttpHeaders.AUTHORIZATION]: bearerAuthHeader(
                await this.getStsService().getToken(true),
              ),
            }
            return retry(request)
          }
        }
      }

      if (response.status >= 400 && response.status < 500) {
        const rawError: RawError = JSON.parse(await response.text())
        throw new UnifiedApiBadRequestError(
          response.status,
          rawError.error.code,
          rawError.error.message,
        )
      }
      if (response.status >= 500) {
        const rawError: RawError = JSON.parse(await response.text())
        throw new UnifiedApiInternalServerError(
          response.status,
          rawError.error.code,
          rawError.error.message,
        )
      }
      return response.text()
    } else {
      return ''
    }
  }

  #errorHandler = (error: IError) => {
    return error
  }
}

export const ClimatePartnerApi = UnifiedApi
